import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSupportTickets, createSupportTicket, getSupportTicket, replyToSupportTicket } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import logo from '../logo.png';
import config from '../config';

function Support({ user, onLogout }) {
  const { showSuccess, showError } = useToast();
  const cartButtonRef = React.useRef(null);
  const { app } = config;
  const APP_NAME = app.name;
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isTicketDetailOpen, setIsTicketDetailOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium',
  });

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      const data = await getSupportTickets(token, false);
      setTickets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch support tickets:', error);
      showError(error.message || 'Failed to load support tickets. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!formData.subject.trim() || !formData.message.trim()) {
      showError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      await createSupportTicket(formData, token);
      showSuccess('Support ticket created successfully! We will get back to you soon.');
      setIsCreateModalOpen(false);
      setFormData({ subject: '', message: '', priority: 'medium' });
      await fetchTickets();
      // Trigger immediate notification refresh
      window.dispatchEvent(new Event('notification-refresh'));
    } catch (error) {
      console.error('Failed to create support ticket:', error);
      showError(error.message || 'Failed to create support ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const ticketPollingIntervalRef = useRef(null);

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setReplyMessage('');
    setIsTicketDetailOpen(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      const fullTicket = await getSupportTicket(ticket.id, token);
      setSelectedTicket(fullTicket);
    } catch (error) {
      console.error('Failed to fetch ticket details:', error);
    }
  };

  // Real-time updates for ticket conversation when modal is open
  useEffect(() => {
    if (isTicketDetailOpen && selectedTicket?.id) {
      const ticketId = selectedTicket.id; // Capture ticket ID
      const pollTicketUpdates = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          if (!token) return;
          const updatedTicket = await getSupportTicket(ticketId, token);
          
          // Use functional update to avoid dependency on selectedTicket
          setSelectedTicket(prevTicket => {
            if (!prevTicket) return updatedTicket;
            
            // Only update if there are new messages or status changed
            const currentLastMessageId = prevTicket.messages && prevTicket.messages.length > 0
              ? prevTicket.messages[prevTicket.messages.length - 1]?.id
              : null;
            const updatedLastMessageId = updatedTicket.messages && updatedTicket.messages.length > 0
              ? updatedTicket.messages[updatedTicket.messages.length - 1]?.id
              : null;
            
            // Update if messages changed or ticket status changed
            if (currentLastMessageId !== updatedLastMessageId || 
                prevTicket.status !== updatedTicket.status) {
              // Also refresh the ticket list to keep it in sync
              fetchTickets();
              return updatedTicket;
            }
            return prevTicket;
          });
        } catch (error) {
          console.error('Failed to poll ticket updates:', error);
        }
      };

      // Poll every 2 seconds for real-time updates
      ticketPollingIntervalRef.current = setInterval(pollTicketUpdates, 2000);

      return () => {
        if (ticketPollingIntervalRef.current) {
          clearInterval(ticketPollingIntervalRef.current);
          ticketPollingIntervalRef.current = null;
        }
      };
    } else {
      // Clear interval when modal closes
      if (ticketPollingIntervalRef.current) {
        clearInterval(ticketPollingIntervalRef.current);
        ticketPollingIntervalRef.current = null;
      }
    }
  }, [isTicketDetailOpen, selectedTicket?.id, fetchTickets]);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!selectedTicket || !replyMessage.trim()) {
      showError('Please enter a message.');
      return;
    }

    if (selectedTicket.status === 'closed' || selectedTicket.status === 'resolved') {
      showError('This ticket is closed. You cannot reply to closed tickets.');
      return;
    }

    setReplying(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      await replyToSupportTicket(selectedTicket.id, replyMessage.trim(), token, false);
      showSuccess('Reply sent successfully!');
      setReplyMessage('');
      
      // Refresh the ticket to get updated messages
      const updatedTicket = await getSupportTicket(selectedTicket.id, token);
      setSelectedTicket(updatedTicket);
      
      // Refresh ticket list
      await fetchTickets();
      // Trigger immediate notification refresh
      window.dispatchEvent(new Event('notification-refresh'));
    } catch (error) {
      console.error('Failed to send reply:', error);
      showError(error.message || 'Failed to send reply. Please try again.');
    } finally {
      setReplying(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-100 text-blue-800 border-blue-300',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      resolved: 'bg-green-100 text-green-800 border-green-300',
      closed: 'bg-gray-100 text-gray-800 border-gray-300',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusCardBg = (status) => {
    const colors = {
      open: 'bg-gradient-to-br from-blue-50/80 via-white to-white border-2 border-blue-300/50 shadow-xl shadow-blue-200/50',
      in_progress: 'bg-gradient-to-br from-yellow-50/80 via-white to-white border-2 border-yellow-300/50 shadow-xl shadow-yellow-200/50',
      resolved: 'bg-gradient-to-br from-green-50/80 via-white to-white border-2 border-green-300/50 shadow-xl shadow-green-200/50',
      closed: 'bg-gradient-to-br from-gray-50/80 via-white to-white border-2 border-gray-300/50 shadow-xl shadow-gray-200/50',
    };
    return colors[status] || 'bg-gradient-to-br from-gray-50/80 via-white to-white border-2 border-gray-300/50 shadow-xl shadow-gray-200/50';
  };

  const getStatusTopBar = (status) => {
    const colors = {
      open: 'from-blue-500 to-blue-600',
      in_progress: 'from-yellow-500 to-yellow-600',
      resolved: 'from-green-500 to-green-600',
      closed: 'from-gray-500 to-gray-600',
    };
    return colors[status] || 'from-gray-500 to-gray-600';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800 border-gray-300',
      medium: 'bg-blue-100 text-blue-800 border-blue-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      urgent: 'bg-red-100 text-red-800 border-red-300',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Calculate statistics
  const totalTickets = tickets.length;
  const openTickets = tickets.filter(t => t.status === 'open').length;
  const inProgressTickets = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedTickets = tickets.filter(t => t.status === 'resolved').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <TopPromoBar appName={APP_NAME} />
      <Header
        logo={logo}
        appName={APP_NAME}
        cartCount={0}
        onCartClick={() => {}}
        cartButtonRef={cartButtonRef}
        searchQuery=""
        onSearchChange={() => {}}
        activeFilter={null}
        onFilterChange={() => {}}
        onCategoryFilterChange={() => {}}
        user={user}
        onLoginSuccess={() => {}}
        onLogout={onLogout}
      />
      
      <div className="pt-[100px] sm:pt-[120px] pb-6 sm:pb-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Header - Sticky */}
          <div className="sticky top-[100px] sm:top-[120px] z-40 mb-6 sm:mb-8 bg-white py-4 sm:py-5 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 border-b border-gray-200 shadow-md">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <div className="flex items-center gap-3 sm:gap-3">
                <div className="w-1.5 h-10 sm:w-1.5 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-2 sm:mb-2">Support Center</h1>
                  <p className="text-base sm:text-base text-gray-600 font-medium">View and manage your support tickets</p>
                </div>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg sm:rounded-xl text-sm sm:text-base font-semibold hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 transform"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">Create New Ticket</span>
                <span className="sm:hidden">New Ticket</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="mb-4 sm:mb-6">
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-blue-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-blue-200/50 hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Total Tickets</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-blue-700 leading-tight">{totalTickets}</p>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-blue-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-blue-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-blue-200/50 hover:shadow-2xl hover:shadow-blue-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Open</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-blue-700 leading-tight">{openTickets}</p>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-yellow-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-yellow-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-yellow-200/50 hover:shadow-2xl hover:shadow-yellow-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">In Progress</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-yellow-700 leading-tight">{inProgressTickets}</p>
                </div>
              </div>
              <div className="relative bg-gradient-to-br from-green-50/80 via-white to-white rounded-lg sm:rounded-2xl border-2 border-green-300/50 p-2 sm:p-4 shadow-lg sm:shadow-xl shadow-green-200/50 hover:shadow-2xl hover:shadow-green-300/50 hover:-translate-y-1 transition-all duration-500 group overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-green-600 opacity-70"></div>
                <div className="relative z-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 sm:mb-2 gap-1">
                    <span className="text-[8px] sm:text-xs font-bold text-gray-900 uppercase tracking-wider leading-tight">Resolved</span>
                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-md sm:rounded-lg flex items-center justify-center text-white shadow-lg self-start sm:self-auto">
                      <svg className="w-3 h-3 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-sm sm:text-2xl font-black text-green-700 leading-tight">{resolvedTickets}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Card */}
          <div className="relative bg-gradient-to-br from-white via-gray-50/30 to-white rounded-2xl border-2 border-gray-300/50 shadow-xl shadow-gray-300/50 overflow-hidden">
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-dark to-secondary opacity-60"></div>
            
            {/* Support Tickets Content */}
            <div className="p-4 sm:p-6">
              {loading ? (
                <div className="text-center py-16">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="text-gray-600 mt-4 font-medium">Loading tickets...</p>
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-20">
                  <div className="relative w-32 h-32 mx-auto mb-6">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl blur-xl opacity-50"></div>
                    <div className="relative w-32 h-32 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl flex items-center justify-center border-2 border-gray-200/60 shadow-lg">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <p className="text-gray-700 font-bold text-xl mb-2">No support tickets yet</p>
                  <p className="text-gray-500 text-sm mb-6">
                    Get started by creating your first support ticket. Our team is ready to assist you!
                  </p>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-primary/30 hover:-translate-y-1 transition-all duration-300 transform"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Your First Ticket
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      onClick={() => handleViewTicket(ticket)}
                      className={`group relative ${getStatusCardBg(ticket.status)} rounded-lg sm:rounded-xl p-4 sm:p-6 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-pointer`}
                    >
                      {/* Top accent line */}
                      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${getStatusTopBar(ticket.status)} opacity-70`}></div>
                      
                      {/* Decorative gradient */}
                      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      <div className="relative flex flex-col gap-3 sm:gap-4">
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-2 mb-3">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900">Ticket #{ticket.id}</h3>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold border w-fit ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ').toUpperCase()}
                            </span>
                            <span className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-bold border w-fit ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority.toUpperCase()}
                            </span>
                          </div>
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">{ticket.subject}</h4>
                          <p className="text-sm sm:text-base text-gray-600 line-clamp-2 mb-3">{ticket.message}</p>
                          <div className="space-y-1.5 sm:space-y-1">
                            <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              Created: {formatDate(ticket.created_at)}
                            </p>
                            {ticket.messages && ticket.messages.some(msg => msg.is_admin) && (
                              <p className="text-xs sm:text-sm text-green-600 flex items-center gap-2 font-medium">
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Admin replied
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTicket(ticket);
                            }}
                            className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 bg-gradient-to-r from-primary via-primary to-primary-dark text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            View Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex items-start justify-center min-h-full px-2 py-8 sm:px-4 sm:py-8 sm:min-h-screen">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-60 backdrop-blur-sm" 
              onClick={() => setIsCreateModalOpen(false)}
            ></div>

            <div className="relative bg-white rounded-xl sm:rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-full sm:max-w-lg md:max-w-xl my-8 sm:my-8 max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-4rem)] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg sm:text-xl font-bold text-white flex-1">Create Support Ticket</h3>
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-all flex-shrink-0"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <form onSubmit={handleCreateTicket}>
                <div className="bg-white px-4 py-5 sm:px-6 sm:py-6 max-h-[70vh] overflow-y-auto">
                  <div className="space-y-5">
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 p-4">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2.5 uppercase tracking-wide">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                        className="w-full px-4 py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white font-medium"
                        placeholder="Brief description of your issue"
                      />
                    </div>

                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-gray-200 p-4">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2.5 uppercase tracking-wide">
                        Priority
                      </label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                        className="w-full px-4 py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white font-medium"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50/50 via-white to-indigo-50/30 rounded-xl border-2 border-blue-200 p-4">
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2.5 uppercase tracking-wide">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={6}
                        className="w-full px-4 py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none bg-white shadow-sm"
                        placeholder="Please describe your issue in detail..."
                      />
                      <p className="mt-2 text-xs text-gray-500 font-medium">Minimum 10 characters required</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row-reverse gap-3 border-t-2 border-gray-200">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-lg border border-transparent shadow-lg px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary to-primary-dark text-sm sm:text-base font-semibold text-white hover:shadow-xl hover:shadow-primary/30 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Ticket
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="w-full sm:w-auto inline-flex justify-center rounded-lg border-2 border-gray-300 shadow-sm px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-sm sm:text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {isTicketDetailOpen && selectedTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-start justify-center min-h-screen px-2 py-4 sm:px-4 sm:py-8">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-60 backdrop-blur-sm" 
              onClick={() => setIsTicketDetailOpen(false)}
            ></div>

            <div className="relative bg-white rounded-xl sm:rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all w-full max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-3xl my-8 sm:my-8 max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-4rem)] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary via-primary-dark to-secondary px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-white">Ticket #{selectedTicket.id}</h3>
                      <span className={`px-2.5 py-1 text-[10px] sm:text-xs font-semibold rounded-full border-2 ${getStatusColor(selectedTicket.status)} bg-white/90 backdrop-blur-sm`}>
                        {selectedTicket.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <span className={`px-2.5 py-1 text-[10px] sm:text-xs font-semibold rounded-full border-2 ${getPriorityColor(selectedTicket.priority)} bg-white/90 backdrop-blur-sm`}>
                        {selectedTicket.priority.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-white/90 font-medium truncate">{selectedTicket.subject}</p>
                  </div>
                  <button
                    onClick={() => setIsTicketDetailOpen(false)}
                    className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-all flex-shrink-0"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="bg-white px-4 py-4 sm:px-6 sm:py-6 overflow-y-auto flex-1 min-h-0" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="space-y-4 sm:space-y-6">
                  {/* Conversation Thread */}
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Conversation</label>
                    <div className="space-y-4">
                      {/* Initial message (from ticket creation) */}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2 sm:gap-3 mb-2">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0 shadow-md ring-2 ring-primary/20">
                            {selectedTicket.user?.name ? selectedTicket.user.name.charAt(0).toUpperCase() : 'U'}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{selectedTicket.user?.name || 'You'}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{formatDate(selectedTicket.created_at)}</p>
                          </div>
                        </div>
                        <div className="ml-10 sm:ml-12 bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-xl p-3 sm:p-4 text-sm sm:text-base text-gray-900 whitespace-pre-wrap border-2 border-gray-200 break-words shadow-sm">
                          {selectedTicket.message}
                        </div>
                      </div>

                      {/* Conversation messages */}
                      {selectedTicket.messages && selectedTicket.messages.length > 0 && selectedTicket.messages.map((msg) => (
                        <div key={msg.id} className={`flex flex-col ${msg.is_admin ? 'items-end' : 'items-start'}`}>
                          <div className={`flex items-center gap-2 sm:gap-3 mb-2 ${msg.is_admin ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0 shadow-md ${
                              msg.is_admin 
                                ? 'bg-gradient-to-br from-blue-500 to-blue-600 ring-2 ring-blue-200' 
                                : 'bg-gradient-to-br from-primary to-primary-dark ring-2 ring-primary/20'
                            }`}>
                              {msg.user?.name ? msg.user.name.charAt(0).toUpperCase() : (msg.is_admin ? 'A' : 'U')}
                            </div>
                            <div className={`min-w-0 flex-1 ${msg.is_admin ? 'text-right' : ''}`}>
                              <p className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                                {msg.is_admin ? (msg.user?.name || 'Admin') : (msg.user?.name || 'You')}
                                {msg.is_admin && <span className="ml-1.5 px-2 py-0.5 text-[10px] text-blue-600 bg-blue-100 rounded-full font-medium">Admin</span>}
                              </p>
                              <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">{formatDate(msg.created_at)}</p>
                            </div>
                          </div>
                          <div className={`max-w-[85%] sm:max-w-[80%] rounded-xl p-3 sm:p-4 text-sm sm:text-base whitespace-pre-wrap border-2 break-words shadow-sm ${
                            msg.is_admin
                              ? 'bg-gradient-to-br from-blue-50 via-blue-50/80 to-indigo-50 border-blue-300 text-gray-900'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}>
                            {msg.message}
                          </div>
                        </div>
                      ))}

                      {(!selectedTicket.messages || selectedTicket.messages.length === 0) && (
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100/50 border-2 border-yellow-300 rounded-xl p-4 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <p className="text-sm sm:text-base font-semibold text-yellow-800">Waiting for admin response...</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reply Section - Only show if ticket is not closed or resolved */}
                  {selectedTicket.status !== 'closed' && selectedTicket.status !== 'resolved' && (
                    <div className="pt-4 border-t border-gray-200">
                      <form onSubmit={handleReply}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {selectedTicket.messages && selectedTicket.messages.length > 0 ? 'Reply to this conversation' : 'Add a reply'}
                        </label>
                        <textarea
                          value={replyMessage}
                          onChange={(e) => setReplyMessage(e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none mb-3"
                          placeholder="Type your reply here..."
                          required
                        />
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => setIsTicketDetailOpen(false)}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                          >
                            Close
                          </button>
                          <button
                            type="submit"
                            disabled={replying || !replyMessage.trim()}
                            className="px-6 py-2 bg-gradient-to-r from-primary to-primary-dark text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                          >
                            {replying ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Sending...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                Send Reply
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {selectedTicket.status === 'closed' && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm font-medium text-gray-800">This ticket is closed. You cannot reply to closed tickets.</p>
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={() => setIsTicketDetailOpen(false)}
                          className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Ticket Metadata */}
                  <div className="pt-4 border-t-2 border-gray-200">
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 p-3 sm:p-4">
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">Created: <span className="text-gray-900">{formatDate(selectedTicket.created_at)}</span></span>
                        </div>
                        {selectedTicket.resolved_at && (
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">Resolved: <span className="text-gray-900">{formatDate(selectedTicket.resolved_at)}</span></span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Support;
